import { CalculatorInputs } from './types';

// Birthday Promo 2026 — single source of truth lives in lib/birthdayPromo.js
// (a plain ESM JS module so the Node API under api/** can import the identical
// values). Re-exported here for frontend consumers (banner, package buttons,
// purchase flow). See lib/birthdayPromo.js for the R1/R6 safety rationale.
export {
  BIRTHDAY_PROMO_CONFIG,
  isBirthdayPromoActive,
  isBirthdayPackageId,
  normalizeBirthdayQty,
  computeBirthdayBreakdown,
} from './lib/birthdayPromo.js';
export type { BirthdayPackageId } from './lib/birthdayPromo.js';

// Single-Share Special (Aug 2026) — single source of truth lives in
// lib/singleShareSpecial.js (plain ESM JS so the Node API under api/** imports
// the identical values). Re-exported here for frontend consumers (first package
// card description, mini countdown, Cravenby donation notice). Display/config
// only here; live bonus minting is gated server-side. See lib/singleShareSpecial.js.
export {
  SINGLE_SHARE_SPECIAL_CONFIG,
  isSingleShareSpecialActive,
  computeSingleShareSpecialBreakdown,
  donationNoticeForDate,
} from './lib/singleShareSpecial.js';

// Production Capital Share Campaign — package matrix SSOT (lib/productionCapitalCampaign.js)
export {
  PRODUCTION_CAPITAL_CAMPAIGN,
  PRODUCTION_CAPITAL_PACKAGES,
  isProductionCapitalPackageId,
  listActiveProductionCapitalPackages,
  getProductionCapitalPackage,
  computeProductionCapitalBreakdown,
  inferProductionCapitalPackage,
  quoteProductionCapitalCart,
  optimizeProductionCapitalSpend,
  adviseProductionCapitalCart,
  canonicalizeProductionCapitalNotes,
  isProductionCapitalDateActive,
  isProductionCapitalCampaignActive,
  computeProductionCapitalCounters,
  validateProductionCapitalPurchase,
  packageInventoryUsed,
} from './lib/productionCapitalCampaign.js';
export type { ProductionCapitalPackageId } from './lib/productionCapitalCampaign.js';

// API Configuration - FORCE VERCEL DETECTION
// Use hostname-based detection as fallback for environment detection issues
const isVercelProduction = typeof window !== 'undefined' &&
  (window.location.hostname.includes('vercel.app') || window.location.hostname.includes('vercel.com'));

// Support both Vite (import.meta.env) and Node.js/Vercel serverless (process.env)
const isViteProd = (import.meta as any).env?.PROD ?? false;
const isServerSide = typeof window === 'undefined';
const isProduction = isViteProd || isVercelProduction || isServerSide;

const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Localhost must use same-origin relative URLs so Vite can proxy /api to
// :8002. Cross-origin calls to :8002 were creating the DB session but the
// browser could not read the session id (CORS), which forced legal-docs and
// profile screens even for complete users.
export const API_BASE_URL = isLocalhost
  ? ''
  : (isProduction
    ? '' // Use relative URLs in production (Vercel serverless functions)
    : ((import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8002'));

// API_BASE_URL stays relative on production and localhost. Page-load hostname
// debug logging was removed.

// Mining Operation Constants
// Revised single-100tph plant baseline (May 2026). Per-plant kg/month =
// TPH × hrs/day × days/month × grade(g/t) / 1000 = 100×10×22.4×0.9/1000 = 20.16 kg.
export const PLANT_CAPACITY_TPH = 100; // Tonnes per hour per plant
export const EFFECTIVE_HOURS_PER_DAY = 10; // Effective operating hours per day
export const OPERATING_DAYS_PER_MONTH = 22.4; // Effective operating days per month
export const OPERATING_DAYS_PER_YEAR = 268.8; // 22.4 × 12
export const BULK_DENSITY_T_PER_M3 = 1.8; // Retained for legacy callers; unused by new formula
export const HA_PER_PLANT = 25; // Hectares of land per wash plant
export const TOTAL_SHARES = 1400000; // Total shares in the project (maximum supply cap)
export const PROJECT_TOTAL_HA = 250; // Total project hectares for 2026

export const DEFAULT_VALUES: CalculatorInputs = {
  landHa: 25, // Base land size - will be overridden by expansion plan
  avgGravelThickness: 0.8,
  inSituGrade: 0.9, // grams of recovered gold per tonne of throughput
  recoveryFactor: 100, // recovery is baked into grade in the new model
  goldPriceUsdPerKg: 150000, // Fixed projection gold price ($150k/kg)
  opexPercent: 45, // as percentage
  userShares: 1, // Start with 1 share
  dividendPayoutPercent: 50, // as percentage
};

// Updated land size options - now up to 5,000 hectares (200 options × 25 ha)
export const LAND_SIZE_OPTIONS = Array.from({ length: 200 }, (_, i) => (i + 1) * 25);

// 5-Year Plant Expansion Plan (June targets)
export const EXPANSION_PLAN = {
  2026: { plants: 10, hectares: 250, month: 'June' },
  2027: { plants: 30, hectares: 750, month: 'June' },
  2028: { plants: 50, hectares: 1250, month: 'June' },
  2029: { plants: 100, hectares: 2500, month: 'June' },
  2030: { plants: 200, hectares: 5000, month: 'June' }
};

export const EXPANSION_YEARS = Object.keys(EXPANSION_PLAN).map(Number);

// Helper function to get expansion plan details
export const getExpansionPlanForYear = (year: number) => {
  return EXPANSION_PLAN[year as keyof typeof EXPANSION_PLAN] || null;
};